import { useMemo } from "react";

import { useNow } from "@/features/shared/hooks/use-now";

import { buildRiskPicture } from "./build-risk-picture";
import { highestRiskPictureLevel } from "./highest-risk-picture-level";
import type { RiskPictureKind, RiskPictureLevel } from "./risk-picture-model";
import { riskPictureNow } from "./risk-picture-now";
import { useRiskPictureWarningSetsQuery } from "./use-risk-picture-warning-sets";

/**
 * Highest land warning level. `kind` limits the scan to issued warnings or
 * kansverwachtingen. Undefined when nothing in the window has a level.
 */
export function useRiskPictureLevel(
  enabled = true,
  kind?: RiskPictureKind,
): RiskPictureLevel | undefined {
  const warningSetsQuery = useRiskPictureWarningSetsQuery({ enabled });
  const tick = useNow(60 * 1000);
  // `riskPictureNow` allocates a new Date per call; pin it to the tick so the memo holds.
  const now = useMemo(() => riskPictureNow(tick), [tick]);

  return useMemo(() => {
    if (!enabled || !warningSetsQuery.data) return undefined;
    return highestRiskPictureLevel(buildRiskPicture(warningSetsQuery.data, { now }), kind);
  }, [enabled, kind, now, warningSetsQuery.data]);
}
